import { awaitingFirstTouch, leadStageKey } from './stageKeys';
import { positionOf } from './leadPosition';
import { categoriesOf } from './leadFields';
import { daysSinceTouch } from './leadActivity';
import type { LeadPosition } from './leadPosition';
import type { LeadDealOutcome } from './dealOutcomes';
import type { LeadCategoryLink } from './leadFields';
import { isDueToday, isRunningOut, loopStateOf, progressOf } from './leadLoop';
import type { LeadLoop } from './leadLoop';
import type { RunningRound } from './leadRounds';
import type { Lead, LeadStage } from './types';

/**
 * The Leads list, as a list.
 *
 * The Pipeline kanban is going away — Kyu: "Once Deals have their own
 * Pipeline, the current lead Pipeline becomes duplicate and confusing."
 * What was good about it moves here as filters, so it can be sorted,
 * combined and exported rather than only looked at:
 *
 *   Backlog drawer  →  Awaiting first touch
 *   Stale chip      →  No activity in N days
 *   Source badge    →  Direction and Source filters
 *
 * Everything here is pure. The old dashboard worked its equivalents out
 * inline inside a 5,000-line component, which is why "awaiting first touch"
 * meant something different on the banner than it did on the tile.
 */

export interface LeadFilter {
  /** Free text over name, company, email, title. */
  query: string;
  /**
   * Empty means every position. Holds the five statuses a human sets plus the
   * two the deals decide — Won and Lost are filterable exactly like the rest,
   * which is the point of deriving them.
   */
  statusKeys: LeadPosition[];
  /** null means either. */
  direction: 'inbound' | 'outbound' | null;
  /** Empty means any category. A lead matches if it holds ANY of them. */
  categoryIds: string[];
  /** Only leads nobody has contacted. */
  awaitingFirstTouchOnly: boolean;
  /** Only leads carrying the nurture badge. */
  nurtureOnly: boolean;
  /** Only leads whose address is known bad. */
  badContactOnly: boolean;
  /** Created within this many days. null means any age. */
  createdWithinDays: number | null;
  /**
   * Only leads whose last touch is older than this many days — and leads never
   * touched at all, because "nobody has followed this up" is the question, and
   * a lead nobody ever contacted is the worst case of it, not an exception.
   *
   * Replaces the Pipeline board's Stale chip, which read touches off list
   * memberships and so could not see a lead that was never on a list.
   */
  staleAfterDays: number | null;
  /**
   * Only leads with no OPEN follow-up task.
   *
   * Alex's worry, in his words: leads falling through the cracks. Every state
   * he named — contacted with nothing scheduled, replied and waiting on us —
   * is this one condition combined with a status he can already pick.
   *
   * Distinct from `staleAfterDays`, and the difference is the whole point.
   * Stale asks whether anything went OUT recently. This asks whether anybody
   * has committed to what happens NEXT. A lead emailed this morning with no
   * follow-up set is not stale and is exactly the lead that gets forgotten.
   */
  noNextActionOnly: boolean;
  /**
   * Only leads with no Lead Source — the Needs review cohort.
   *
   * Not a chip. This is one of the four Leads sub-tabs, and it lives in the
   * filter rather than in its own piece of state so that the tab and the
   * filter cannot disagree. A saved view that sets `direction: 'inbound'`
   * lights the Inbound tab for free, which is what two sources of truth would
   * have had to be taught by hand.
   */
  noSourceOnly: boolean;
  /**
   * Only leads with no Lead Origin.
   *
   * Deliberately NOT part of Needs review. Lead Origin was off every form from
   * batch 49 until Drop 2c, so nearly every older lead is missing one —
   * folding it into the tab would make the tab the whole database and bury the
   * Lead Source gap inside it. As a chip it composes with any tab, so "Inbound
   * leads with no origin" is still one click.
   */
  noOriginOnly: boolean;
  /**
   * Only leads with no Capture method recorded.
   *
   * It starts blank on all 690 existing leads, because nothing was backfilled
   * — the only value to guess from is the origin, and "Typeform means the
   * website form" is a guess wearing a fact's clothes. So the blank has to be
   * FINDABLE, or the field quietly stays empty forever.
   *
   * Same shape and the same argument as noOriginOnly, which exists because
   * Lead Origin was off every form for months.
   */
  noCaptureMethodOnly: boolean;
  /**
   * The Lead Origin GROUP to narrow to — 'Social media', 'Forms', 'Referral'.
   * null means every origin.
   *
   * Two fields rather than one list of ids, because the two answer different
   * questions and a single list could not tell them apart: "every social lead"
   * and "Instagram specifically" would both arrive as a set of ids, and the
   * chips could not be redrawn from it without guessing. See leadOriginFilter.
   */
  originGroup: string | null;
  /**
   * One value inside that group, or null for the whole group. Meaningless
   * without `originGroup` and always cleared alongside it — picking a group
   * and keeping the previous group's platform is the bug this pairing exists
   * to make impossible.
   */
  originId: string | null;
  /**
   * Only leads with no company at all — neither a linked company row nor a
   * name typed into the text field.
   *
   * MEASURED, 27 Aug 2026: 228 of 690 leads are in this state. A third of the
   * database, and until now there was no way to see them as a group.
   *
   * Kyu asked for these to be "tagged" by writing "n/a" into company_name.
   * They are tagged here instead, because `company_name` is what `{{company}}`
   * resolves to in email copy — replaceTokens() in types.ts substitutes it
   * directly — so 228 rows reading "n/a" would send "I see you're at n/a" to
   * 228 people the first time somebody used that token. A filter answers the
   * same question and cannot reach an inbox.
   */
  noCompanyOnly: boolean;
  /**
   * One evidence group inside Needs Review, or null for all of them.
   *
   * A string rather than a union because the import batches are discovered
   * from the data — "everything Karl added on the 14th" is a group nobody
   * could have declared in advance, and hardcoding today's batches would go
   * stale the next time somebody imports a spreadsheet.
   *
   * Only meaningful on the Needs Review tab, and `withLeadTab` clears it on
   * the way out: an evidence group is a way of working through unclassified
   * leads, and every lead on the other tabs is already classified.
   */
  evidenceGroup: string | null;
  /**
   * Which closing reasons to narrow to. Empty means any — including the leads
   * that are not closed at all.
   *
   * Kyu's question, and the only reason this exists: "who are the people that
   * said no this year? ... so we know who to go back to when the time comes."
   *
   * IDs rather than names, because the reasons are rows an admin can rename —
   * the same argument that gave `lead_stages` its `key`, and the same class of
   * mistake that once reported 766 outbound leads against 98 tagged. The chips
   * are built from `lead_closure_reasons` at render time, so the eighth reason
   * needs no code.
   *
   * Picking a reason implicitly narrows to Closed leads, because the guard in
   * 20261122000100 nulls the reason on anything else. Nothing lights the
   * Closed status chip alongside it: one decision must not read as two.
   */
  closedReasonIds: string[];
  /**
   * Closed within this many days. null means any date — and that includes the
   * leads closed before 20261219000100, whose date was never recorded and was
   * deliberately not invented.
   *
   * "Within days" rather than a calendar year, the same shape as
   * `createdWithinDays` and `staleAfterDays`. This is a re-engagement list: in
   * August, "this year" is eight months that stop dead at 1 January, and
   * somebody who said not right now last November is exactly the one to call.
   */
  closedWithinDays: number | null;
  /**
   * One queue in the follow-up loop, or null for all of them.
   *
   * SINGLE-SELECT, unlike statusKeys and categoryIds, and the reason is what
   * these are for. A status filter narrows a list somebody is reading; a loop
   * filter picks the queue somebody is going to WORK. "Due today or never in a
   * round" is not a morning's work, it is two.
   *
   * The five overlap on purpose — Behind is a subset of Due today, which is a
   * subset of In a round — because they are not categories, they are
   * increasingly urgent slices of the same pile.
   */
  loop: LoopFilter | null;
  /**
   * Only these leads, by id. null means no restriction.
   *
   * Unlike every other condition in this interface, this is not a question
   * about a lead. It is an ANSWER somebody already computed: the set behind a
   * number they clicked on the Pipeline, the Dashboard or a report.
   *
   * It exists because those sets are not expressible as filters. "Arrived
   * inside this window, carries a logged touch, and matches whatever was on
   * screen at the time" is a funnel step, and inventing conditions here until
   * it could be expressed would put a second definition of every funnel step
   * in this file, free to drift from the first. So the number hands over its
   * own answer, this field carries it, and the definition stays in the lens
   * that computed it.
   *
   * ANDed with everything else, deliberately. Drilling into "Emailed · 2" and
   * then typing a name narrows those two rather than searching the whole
   * table. The banner above the table says which set is applied, so somebody
   * who finds nothing can see why.
   *
   * An EMPTY array is a real answer meaning nothing matched, and is not the
   * same as null. A number reading zero must open an empty table, never the
   * entire lead list.
   */
  onlyLeadIds: readonly string[] | null;
}

/**
 * The five queues, from most urgent to least.
 *
 *   behind             the pace owes more than one touch nobody has sent
 *   due-today          it owes at least one
 *   in-round           a round is running, on pace or not
 *   needs-next-round   a round ENDED and nobody scheduled the next
 *   never-in-a-round   nobody has begun
 *
 * `needs-next-round` is the one that cannot be seen any other way, and it is
 * the failure state in Kyu's rule: a lead somebody dropped looks exactly like
 * a lead nobody started, unless the two are told apart.
 */
export type LoopFilter =
  'running-out' | 'due-today' | 'in-round' | 'needs-next-round' | 'never-in-a-round';

export const LOOP_FILTERS: { value: LoopFilter; label: string; hint: string }[] = [
  {
    value: 'due-today',
    label: 'Due today',
    hint: 'As many follow-ups left as days left, so today is one of the days this round has to use.',
  },
  {
    // Was "Behind", which named a fault rather than a fact. Kyu, 29 Aug: "3/7
    // behind — well are we really behind, or are we just at the 3rd email
    // yet?" This one is arithmetic: more follow-ups owed than days to send
    // them in, so the round cannot be finished without acting today.
    value: 'running-out',
    label: 'Running out of days',
    hint: 'More follow-ups left than days left, so this round cannot be finished unless somebody acts.',
  },
  { value: 'in-round', label: 'In a round', hint: 'A follow-up round is running, on pace or not.' },
  {
    value: 'needs-next-round',
    label: 'Needs next round',
    hint: 'A round ended and nobody has scheduled the next one. These carry an overdue task.',
  },
  {
    value: 'never-in-a-round',
    label: 'Never in a round',
    hint: 'No round has ever been started on this lead.',
  },
];

export function isLoopFilter(value: string): value is LoopFilter {
  return LOOP_FILTERS.some((entry) => entry.value === value);
}

/**
 * What the loop filters need to answer, gathered once for the whole list.
 *
 * One object rather than two more positional arguments: `filterLeads` already
 * takes five optional maps, and the sixth and seventh would have made the call
 * site a row of `new Map()` placeholders nobody can read.
 */
export interface RoundContext {
  running: ReadonlyMap<string, RunningRound>;
  loops: ReadonlyMap<string, LeadLoop>;
}

export const EMPTY_ROUNDS: RoundContext = { running: new Map(), loops: new Map() };

/** True when a lead belongs in the queue somebody picked. */
export function matchesLoop(
  leadId: string,
  want: LoopFilter,
  rounds: RoundContext,
  todayIso: string,
): boolean {
  const state = loopStateOf(rounds.loops.get(leadId));
  if (want === 'never-in-a-round') {
    return state === 'never-in-a-round';
  }
  if (want === 'needs-next-round') {
    return state === 'needs-next-round';
  }
  if (state !== 'in-round') {
    return false;
  }
  if (want === 'in-round') {
    return true;
  }
  const progress = progressOf(rounds.running.get(leadId), todayIso);
  return want === 'running-out' ? isRunningOut(progress) : isDueToday(progress);
}

export const EMPTY_FILTER: LeadFilter = {
  query: '',
  statusKeys: [],
  direction: null,
  categoryIds: [],
  awaitingFirstTouchOnly: false,
  nurtureOnly: false,
  badContactOnly: false,
  createdWithinDays: null,
  staleAfterDays: null,
  noNextActionOnly: false,
  noSourceOnly: false,
  noOriginOnly: false,
  noCaptureMethodOnly: false,
  originGroup: null,
  originId: null,
  noCompanyOnly: false,
  evidenceGroup: null,
  closedReasonIds: [],
  closedWithinDays: null,
  loop: null,
  onlyLeadIds: null,
};

function matchesQuery(lead: Lead, needle: string): boolean {
  if (needle === '') {
    return true;
  }
  return [lead.first_name, lead.last_name, lead.company_name, lead.email, lead.title]
    .join(' ')
    .toLowerCase()
    .includes(needle);
}

function daysBetween(fromIso: string, toIso: string): number {
  const from = new Date(fromIso).getTime();
  const to = new Date(`${toIso}T23:59:59Z`).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) {
    return Number.POSITIVE_INFINITY;
  }
  return Math.floor((to - from) / 86_400_000);
}

/**
 * Every condition is an AND; a list within a condition is an OR.
 *
 * So "Corporate Gifting OR Partnerships, inbound, in the last 30 days" is one
 * filter rather than three saved views — which is what makes the saved views
 * below thin enough to be worth having.
 */
export function filterLeads(
  leads: Lead[],
  filter: LeadFilter,
  stages: LeadStage[],
  links: LeadCategoryLink[],
  todayIso: string,
  /**
   * Lead ids that have an OPEN follow-up. Defaults to empty, which makes
   * `noNextActionOnly` match every lead rather than none — the honest failure
   * direction if the follow-up query has not answered yet, because showing
   * everything is visibly wrong while showing nothing looks like a real answer.
   */
  leadsWithNextAction: ReadonlySet<string> = new Set(),
  /** Lead id → evidence group, from leadEvidence.ts. Empty until it loads. */
  evidenceByLead: ReadonlyMap<string, string> = new Map(),
  /**
   * Lead id → deal rollup, which is what turns a Converted lead into Won or
   * Lost. Defaults to empty so an unloaded rollup reads every Converted lead
   * as Converted — the stage as set, never a guessed outcome.
   */
  outcomesByLead: ReadonlyMap<string, LeadDealOutcome> = new Map(),
  /**
   * The rounds every loop filter reads. Defaults to empty, which makes
   * `never-in-a-round` match everything and the other four match nothing —
   * the honest direction while the queries are in flight, because an
   * over-large list is visibly wrong and an empty one looks like an answer.
   */
  rounds: RoundContext = EMPTY_ROUNDS,
  /**
   * Origin id → the group it belongs to, from leadOriginFilter. Defaults to
   * empty, which makes the origin filter INERT rather than exclusive: until
   * the vocabulary loads there is no honest way to say which leads are social,
   * and an empty list looks like a real answer while the full one is visibly
   * unfiltered.
   */
  originGroupById: ReadonlyMap<string, string> = new Map(),
): Lead[] {
  const needle = filter.query.trim().toLowerCase();
  // Built once rather than per lead: `includes` on a 461-element array inside
  // a filter over every lead is the kind of quadratic nobody notices until the
  // list grows.
  const only = filter.onlyLeadIds === null ? null : new Set(filter.onlyLeadIds);
  return leads.filter((lead) => {
    // First, because it is the cheapest exclusion and by far the most
    // selective one in the list.
    if (only !== null && !only.has(lead.id)) {
      return false;
    }
    if (!matchesQuery(lead, needle)) {
      return false;
    }
    if (filter.statusKeys.length > 0) {
      const position = positionOf(leadStageKey(lead.stage_id, stages), outcomesByLead.get(lead.id));
      if (position === null || !filter.statusKeys.includes(position)) {
        return false;
      }
    }
    if (filter.direction !== null && lead.source !== filter.direction) {
      return false;
    }
    if (filter.categoryIds.length > 0) {
      const mine = categoriesOf(lead.id, links);
      if (!mine.some((id) => filter.categoryIds.includes(id))) {
        return false;
      }
    }
    if (filter.awaitingFirstTouchOnly && !awaitingFirstTouch(lead, stages)) {
      return false;
    }
    if (filter.nurtureOnly && !lead.nurture_active) {
      return false;
    }
    if (filter.badContactOnly && !lead.bad_contact) {
      return false;
    }
    if (
      filter.createdWithinDays !== null &&
      daysBetween(lead.created_at, todayIso) > filter.createdWithinDays
    ) {
      return false;
    }
    if (filter.staleAfterDays !== null) {
      const since = daysSinceTouch(lead.last_touch_at, todayIso);
      if (since !== null && since < filter.staleAfterDays) {
        return false;
      }
    }
    if (filter.noNextActionOnly && leadsWithNextAction.has(lead.id)) {
      return false;
    }
    // `source` is the Lead Source enum; `lead_source_id` is the Lead Origin
    // row. The names are the wrong way round in the database and renaming the
    // columns is a migration nobody needs — so the mapping is written down
    // here, once, rather than re-derived by whoever reads this next.
    if (filter.noSourceOnly && lead.source !== null) {
      return false;
    }
    if (filter.noOriginOnly && lead.lead_source_id !== null) {
      return false;
    }
    if (filter.noCaptureMethodOnly && lead.capture_method_id !== null) {
      return false;
    }
    // Lead Origin, in two steps. The GROUP alone already filters — "how many
    // came from social" is the common question and it costs one chip — and
    // `originId` narrows it when somebody wants one platform.
    if (filter.originGroup !== null && originGroupById.size > 0) {
      const originId = lead.lead_source_id;
      if (originId === null) {
        return false;
      }
      if (filter.originId !== null) {
        if (originId !== filter.originId) {
          return false;
        }
      } else if (originGroupById.get(originId) !== filter.originGroup) {
        return false;
      }
    }
    // Both halves. A lead with a company row linked has a company; so does one
    // with only the text filled in, which is the seven that are named but not
    // linked. Neither is "no company".
    if (filter.noCompanyOnly && (lead.company_id !== null || lead.company_name.trim() !== '')) {
      return false;
    }
    // ── Why it ended, and when ──────────────────────────────────────────────
    // Both read the lead's own columns, so neither needs a map that might not
    // have loaded — unlike the origin and evidence filters below, these are
    // never inert and never guess.
    if (
      filter.closedReasonIds.length > 0 &&
      (lead.closure_reason_id === null || !filter.closedReasonIds.includes(lead.closure_reason_id))
    ) {
      return false;
    }
    // A null date FAILS a date window, and that is the honest answer rather
    // than a kindness. `closed_on` is null on the leads closed before
    // 20261219000100 — nothing was backfilled — so letting them through would
    // put leads whose closing date nobody knows into "closed in the last 90
    // days", which is precisely the claim the filter is asked to make.
    if (filter.closedWithinDays !== null) {
      if (lead.closed_on === null) {
        return false;
      }
      if (daysBetween(lead.closed_on, todayIso) > filter.closedWithinDays) {
        return false;
      }
    }
    // An empty map means the grouping has not been computed — the lists or
    // memberships have not loaded yet. Narrow by nothing rather than by
    // everything: showing the whole queue is visibly not a group, while
    // showing none of it looks like a real and very tidy answer.
    if (filter.loop !== null && !matchesLoop(lead.id, filter.loop, rounds, todayIso)) {
      return false;
    }
    if (filter.evidenceGroup !== null && evidenceByLead.size > 0) {
      if (evidenceByLead.get(lead.id) !== filter.evidenceGroup) {
        return false;
      }
    }
    return true;
  });
}

/**
 * The three views worth one click.
 *
 * Was six. `Awaiting first touch` was the same list as its inbound sibling
 * with the urgent half diluted; `In nurture` and `Bad contact` each duplicated
 * a chip that is still there in the Only group.
 *
 * The line: a saved view is a question somebody asks on a normal working
 * morning and wants named. Everything else is narrowing, and narrowing is what
 * the chips are for. Six named answers to three questions is how a list stops
 * being trusted.
 *
 * A saved view is a filter with a name, not a stored query — so it cannot
 * drift from what the filters do, and editing one on screen just makes it a
 * filter again.
 */
export interface SavedView {
  key: string;
  label: string;
  description: string;
  /** Filled in from the live vocabulary, since categories are rows. */
  build: (categoryIdByName: Map<string, string>) => LeadFilter;
}

export const SAVED_VIEWS: SavedView[] = [
  {
    key: 'inbound-awaiting',
    label: 'Inbound awaiting first touch',
    description:
      'They came to us and nobody has replied. The shortest list with the most money on it.',
    build: () => ({ ...EMPTY_FILTER, awaitingFirstTouchOnly: true, direction: 'inbound' }),
  },
  {
    // Renamed from "Needs a follow-up". That name meant "nothing has gone out
    // in 14 days" and now collides with the three views below, which mean
    // "nothing is scheduled". Two different questions; the old label was
    // actively misleading once both existed. The filter is unchanged.
    key: 'no-follow-up',
    label: 'No contact in 14 days',
    description: 'Nothing has gone out to these in 14 days, including leads never contacted.',
    build: () => ({ ...EMPTY_FILTER, staleAfterDays: 14 }),
  },
  {
    // One of three drafted, and the only one shipped. Kyu: "I want to keep the
    // system lean and avoid clutter, so do not add all three saved views."
    //
    // This is the one that earns a name. A lead who answered and has nothing
    // scheduled is money sitting still, and it is the shortest list with the
    // most on it — the same reasoning that justifies `inbound-awaiting`.
    //
    // The other two — "Contacted, nothing scheduled" and "Engaged and going
    // quiet" — are one extra click away with the status chip plus the No next
    // action chip, and narrowing is what chips are for.
    key: 'replied-no-next',
    label: 'Replied, waiting on us',
    description: 'They answered and no follow-up is set. The most expensive list to leave alone.',
    build: () => ({ ...EMPTY_FILTER, statusKeys: ['engaged'], noNextActionOnly: true }),
  },
  {
    key: 'cg-recent-inbound',
    label: 'Corporate Gifting — Recent Inbound',
    description: 'Inbound Corporate Gifting leads from the last 30 days.',
    build: (byName) => ({
      ...EMPTY_FILTER,
      direction: 'inbound',
      categoryIds: idsFor(byName, ['Corporate Gifting']),
      createdWithinDays: 30,
    }),
  },
];

function idsFor(byName: Map<string, string>, names: string[]): string[] {
  return names.map((name) => byName.get(name)).filter((id): id is string => id !== undefined);
}

/** Lookup for the saved views, built from whatever categories exist today. */
export function categoryIdByName(categories: { id: string; name: string }[]): Map<string, string> {
  return new Map(categories.map((category) => [category.name, category.id]));
}

/** Whether anything is narrowing the list, for the "clear filters" affordance. */
export function isFiltering(filter: LeadFilter): boolean {
  return (
    filter.query.trim() !== '' ||
    filter.statusKeys.length > 0 ||
    filter.direction !== null ||
    filter.categoryIds.length > 0 ||
    filter.awaitingFirstTouchOnly ||
    filter.nurtureOnly ||
    filter.badContactOnly ||
    filter.createdWithinDays !== null ||
    filter.staleAfterDays !== null ||
    filter.noNextActionOnly ||
    filter.noSourceOnly ||
    filter.noOriginOnly ||
    filter.originGroup !== null ||
    // THREE THAT WERE MISSING, found while adding the origin filter and fixed
    // in the same drop: noCaptureMethodOnly (Drop 23), noCompanyOnly (Drop 24)
    // and loop (Drop 22) all narrow the list and none of them said so, so
    // "Clear filters" did not appear when one of them was the only thing on.
    filter.noCaptureMethodOnly ||
    filter.noCompanyOnly ||
    filter.loop !== null ||
    filter.evidenceGroup !== null ||
    // Drop 38. Added here, in sameFilter and in hiddenFilterCount in the same
    // change — see the CASES table in leadFilters.test.ts, which is what makes
    // that a closed trap rather than a remembered one.
    filter.closedReasonIds.length > 0 ||
    filter.closedWithinDays !== null
  );
}

/**
 * Two filters that would return the same leads.
 *
 * Order within a list does not matter — statusKeys ['new','closed'] and
 * ['closed','new'] are the same question — so the lists are compared as sets.
 * Written out field by field rather than by JSON.stringify for exactly that
 * reason: stringify would call those two different and the active-view badge
 * would flicker off when somebody toggled a chip off and back on.
 */
export function sameFilter(a: LeadFilter, b: LeadFilter): boolean {
  const sameSet = (left: string[], right: string[]) =>
    left.length === right.length && left.every((entry) => right.includes(entry));
  return (
    a.query.trim() === b.query.trim() &&
    sameSet(a.statusKeys, b.statusKeys) &&
    a.direction === b.direction &&
    sameSet(a.categoryIds, b.categoryIds) &&
    a.awaitingFirstTouchOnly === b.awaitingFirstTouchOnly &&
    a.nurtureOnly === b.nurtureOnly &&
    a.badContactOnly === b.badContactOnly &&
    a.createdWithinDays === b.createdWithinDays &&
    a.staleAfterDays === b.staleAfterDays &&
    a.noNextActionOnly === b.noNextActionOnly &&
    a.noSourceOnly === b.noSourceOnly &&
    a.noOriginOnly === b.noOriginOnly &&
    a.originGroup === b.originGroup &&
    a.originId === b.originId &&
    a.noCaptureMethodOnly === b.noCaptureMethodOnly &&
    a.noCompanyOnly === b.noCompanyOnly &&
    a.loop === b.loop &&
    a.evidenceGroup === b.evidenceGroup &&
    sameSet(a.closedReasonIds, b.closedReasonIds) &&
    a.closedWithinDays === b.closedWithinDays
  );
}

/**
 * Which saved view the list is currently showing, or null.
 *
 * Measured on the live page before this: clicking a saved view lit up three
 * chips and changed nothing else, so all three view buttons stayed visually
 * identical and nothing said which list you were looking at. That is the first
 * of the four questions the page is supposed to answer on landing.
 *
 * Exact match, deliberately. Adjust one chip after choosing a view and you are
 * no longer looking at that named list — you are looking at something near it,
 * and saying otherwise would make the badge a decoration. The existing
 * behaviour where choosing a view FILLS THE CONTROLS IN rather than hiding
 * them is what makes that safe: adjusting from a view is still one click, you
 * just stop being told you are in it.
 */
export function activeViewKey(
  filter: LeadFilter,
  categoryIdByNameMap: Map<string, string>,
): string | null {
  const match = SAVED_VIEWS.find((view) => sameFilter(filter, view.build(categoryIdByNameMap)));
  return match?.key ?? null;
}

/**
 * One removable condition in the active-filter summary.
 *
 * `clear` returns the filter WITHOUT this condition rather than mutating, so
 * the summary row is pure presentation and a token cannot disagree with the
 * control it mirrors.
 */
export interface FilterToken {
  key: string;
  label: string;
  clear: (filter: LeadFilter) => LeadFilter;
}

/** The Flags group — the five properties a lead either carries or does not. */
const FLAG_TOKENS: { key: keyof LeadFilter; label: string }[] = [
  { key: 'awaitingFirstTouchOnly', label: 'Awaiting first touch' },
  { key: 'nurtureOnly', label: 'In nurture' },
  { key: 'badContactOnly', label: 'Bad contact' },
  // The chip matters more than the saved views built on it: a view cannot
  // express "Inbound + LinkedIn Ads + nothing scheduled", and that combination
  // is the list Alex actually asked for.
  { key: 'noNextActionOnly', label: 'No next action' },
  { key: 'noOriginOnly', label: 'No Lead Origin' },
  // Added Drop 44 with the origin, loop and evidence tokens below. All five
  // could narrow the list without appearing here, which is the exact fault
  // this row exists to prevent — see the block comment on the function.
  { key: 'noCaptureMethodOnly', label: 'No capture method' },
  { key: 'noCompanyOnly', label: 'No company' },
];

/**
 * Everything currently narrowing the list, as removable tokens.
 *
 * This is the piece that has to exist BEFORE any control is hidden. Until
 * now the chips were doing two jobs — they were the control surface AND the
 * only display of what was active — so putting the low-frequency ones away
 * would have made the page quieter and less honest at the same time. The
 * summary takes over the readout job, which is what frees the chips to move.
 */
export function activeFilterTokens(
  filter: LeadFilter,
  labels: {
    status: (key: LeadPosition) => string;
    category: (id: string) => string | null;
    /** Names a closing reason. Null while the vocabulary is still loading. */
    closureReason: (id: string) => string | null;
    /**
     * Names the origin narrowing: the platform if one was picked, otherwise
     * the group. Null while the vocabulary is still loading, which is why the
     * token falls back to the group name it already holds rather than
     * disappearing.
     */
    origin: (group: string, id: string | null) => string | null;
    /** Names a follow-up queue. */
    loop: (value: LoopFilter) => string;
    /** Names an evidence group. Null once the group no longer exists. */
    evidence: (key: string) => string | null;
  },
): FilterToken[] {
  const tokens: FilterToken[] = [];

  if (filter.query.trim() !== '') {
    tokens.push({
      key: 'query',
      label: `“${filter.query.trim()}”`,
      clear: (current) => ({ ...current, query: '' }),
    });
  }

  for (const key of filter.statusKeys) {
    tokens.push({
      key: `status:${key}`,
      label: labels.status(key),
      clear: (current) => ({
        ...current,
        statusKeys: current.statusKeys.filter((entry) => entry !== key),
      }),
    });
  }

  // Direction and noSourceOnly get no token. They are the sub-tab now, and the
  // tab is always on screen with its own count — a second removable copy of it
  // down here would let somebody clear their tab from a row that looks like it
  // is about filters, and land in a list they did not ask for.

  for (const id of filter.categoryIds) {
    // A category that has since been deleted still narrows the list, so it
    // still gets a token — falling back to its id would be unreadable, and
    // dropping it silently would hide a condition that is doing real work.
    tokens.push({
      key: `category:${id}`,
      label: labels.category(id) ?? 'Unknown category',
      clear: (current) => ({
        ...current,
        categoryIds: current.categoryIds.filter((entry) => entry !== id),
      }),
    });
  }

  for (const flag of FLAG_TOKENS) {
    if (filter[flag.key] === true) {
      tokens.push({
        key: `flag:${String(flag.key)}`,
        label: flag.label,
        clear: (current) => ({ ...current, [flag.key]: false }),
      });
    }
  }

  if (filter.staleAfterDays !== null) {
    const days = filter.staleAfterDays;
    tokens.push({
      key: 'stale',
      label: `No touch in ${String(days)} days`,
      clear: (current) => ({ ...current, staleAfterDays: null }),
    });
  }

  if (filter.createdWithinDays !== null) {
    const days = filter.createdWithinDays;
    tokens.push({
      key: 'created',
      // "Added in the last N days", not "Last N days". The summary row now
      // also carries "Closed in the last N days", and a token with no subject
      // beside one that has a subject reads as though they are the same axis.
      label: `Added in the last ${String(days)} days`,
      clear: (current) => ({ ...current, createdWithinDays: null }),
    });
  }

  for (const id of filter.closedReasonIds) {
    // Same rule as the category tokens: a reason that has since been
    // deactivated still narrows the list, so it still gets a token. Falling
    // back to the id would be unreadable and dropping it would hide a
    // condition doing real work.
    tokens.push({
      key: `closed:${id}`,
      label: `Closed: ${labels.closureReason(id) ?? 'unknown reason'}`,
      clear: (current) => ({
        ...current,
        closedReasonIds: current.closedReasonIds.filter((entry) => entry !== id),
      }),
    });
  }

  if (filter.closedWithinDays !== null) {
    const days = filter.closedWithinDays;
    tokens.push({
      key: 'closed-within',
      // "Closed in the last…" rather than "Last 365 days", which is already
      // what the createdWithinDays token says. Two tokens reading "Last 30
      // days" side by side, one about arriving and one about ending, is a
      // summary row that stops being worth reading.
      label: `Closed in the last ${String(days)} days`,
      clear: (current) => ({ ...current, closedWithinDays: null }),
    });
  }

  /**
   * Origin and platform as ONE token, matching the badge count.
   *
   * They are one decision made in two fields — you pick Advertising, then
   * optionally LinkedIn Ads — so two removable chips would invite somebody to
   * clear the platform and be left filtered to a group they thought they had
   * removed. Clearing this clears both.
   */
  if (filter.originGroup !== null) {
    const group = filter.originGroup;
    tokens.push({
      key: 'origin',
      label: labels.origin(group, filter.originId) ?? group,
      clear: (current) => ({ ...current, originGroup: null, originId: null }),
    });
  }

  if (filter.loop !== null) {
    const loop = filter.loop;
    tokens.push({
      key: 'loop',
      label: labels.loop(loop),
      clear: (current) => ({ ...current, loop: null }),
    });
  }

  if (filter.evidenceGroup !== null) {
    const group = filter.evidenceGroup;
    tokens.push({
      key: 'evidence',
      // Same rule as a deleted category: a group that no longer exists still
      // narrows the list, so it still gets a token rather than vanishing.
      label: labels.evidence(group) ?? 'Unknown group',
      clear: (current) => ({ ...current, evidenceGroup: null }),
    });
  }

  return tokens;
}

/**
 * How many of the collapsed groups are currently narrowing the list.
 *
 * Direction, Category and Flags move behind a disclosure; Search and Status
 * stay out. A collapsed control that gives no sign it is holding an active
 * filter is how a list silently lies about what it is showing, so the count
 * rides on the button itself — one of three independent ways this pass keeps
 * hidden state visible, alongside the summary row and the open-on-load rule.
 */
export function hiddenFilterCount(filter: LeadFilter): number {
  return (
    // Direction is not counted: it is the sub-tab, which is never collapsed.
    filter.categoryIds.length +
    (filter.awaitingFirstTouchOnly ? 1 : 0) +
    (filter.nurtureOnly ? 1 : 0) +
    (filter.badContactOnly ? 1 : 0) +
    (filter.noNextActionOnly ? 1 : 0) +
    (filter.noOriginOnly ? 1 : 0) +
    // Counted ONCE whether or not a platform is chosen. "Social media" and
    // "Social media, Instagram" are one narrowing, and counting the platform
    // separately would make the badge say 2 for a single decision.
    (filter.originGroup === null ? 0 : 1) +
    (filter.noCaptureMethodOnly ? 1 : 0) +
    (filter.noCompanyOnly ? 1 : 0) +
    (filter.loop === null ? 0 : 1) +
    (filter.staleAfterDays === null ? 0 : 1) +
    (filter.createdWithinDays === null ? 0 : 1) +
    // Counted like categoryIds — one per reason chosen, because picking three
    // reasons is three separate narrowings somebody can remove one at a time,
    // unlike originGroup + originId which is one decision in two fields.
    filter.closedReasonIds.length +
    (filter.closedWithinDays === null ? 0 : 1)
  );
}

/**
 * ── The Leads sub-tabs ──────────────────────────────────────────────────────
 *
 * Measured on the live Dashboard before this was built: 688 leads — 21
 * inbound, 98 outbound, 569 with no Lead Source. 83% of every lead in the
 * tracker is unclassified.
 *
 * That number decided the shape. Sub-tabs that filter on Lead Source would put
 * 21 leads in one tab, 98 in another, and leave 569 in no tab at all — a Leads
 * screen that quietly omits five leads in six. So Needs review is not an extra
 * view somebody remembers to click. It is the fourth tab, and it exists so
 * that every lead is somewhere.
 *
 * Kyu, choosing this over a fifth saved view: "Needs Review is too large to
 * hide as just a saved view. It should be a real sub-tab because it's a major
 * operational bucket, not an occasional filter."
 *
 * ── Why the tab is derived rather than stored ───────────────────────────────
 *
 * The tab IS the filter's direction/noSourceOnly axis, read back. Holding it
 * as its own useState next to a filter that also carries `direction` is two
 * records of one fact, and they drift: a saved view that sets
 * `direction: 'inbound'` would light no tab, or the wrong one, until somebody
 * remembered to teach it. Derived, that works with no code at all.
 *
 * The Lead Source chips came OUT of the filter panel in the same change. Two
 * controls for one axis is how they end up disagreeing on screen.
 */
export type LeadTab = 'all' | 'inbound' | 'outbound' | 'needs-review';

export const LEAD_TABS: { key: LeadTab; label: string; description: string }[] = [
  { key: 'all', label: 'All Leads', description: 'Every lead, however it arrived.' },
  { key: 'inbound', label: 'Inbound', description: 'They came to us. The consent record says so.' },
  { key: 'outbound', label: 'Outbound', description: 'We went to them.' },
  {
    key: 'needs-review',
    label: 'Needs Review',
    description: 'No Lead Source recorded, so these count as neither Inbound nor Outbound.',
  },
];

/** Which sub-tab the current filter is showing. */
export function activeLeadTab(filter: LeadFilter): LeadTab {
  // Needs review wins the read: `withLeadTab` never builds a filter carrying
  // both, but a hand-built one asking for "no source AND inbound" describes an
  // empty set, and answering "Needs review, and it is empty" is more useful
  // than answering "Inbound" about a list that holds nothing.
  if (filter.noSourceOnly) {
    return 'needs-review';
  }
  if (filter.direction === 'inbound') {
    return 'inbound';
  }
  if (filter.direction === 'outbound') {
    return 'outbound';
  }
  return 'all';
}

/**
 * Move to a sub-tab, keeping every other condition.
 *
 * Switching tabs keeps your search, your status chips and your category — the
 * tab is which cohort you are asking about, not a reset. Clearing everything
 * is what Clear filters is for.
 */
export function withLeadTab(filter: LeadFilter, tab: LeadTab): LeadFilter {
  return {
    ...filter,
    direction: tab === 'inbound' ? 'inbound' : tab === 'outbound' ? 'outbound' : null,
    noSourceOnly: tab === 'needs-review',
    // Leaving Needs Review drops the evidence group. Every lead on the other
    // three tabs already has a Lead Source, so an evidence group there would
    // narrow a list by a question that has been answered.
    evidenceGroup: tab === 'needs-review' ? filter.evidenceGroup : null,
  };
}

/**
 * How many leads each sub-tab would show, given everything else in the filter.
 *
 * The other conditions are applied first and the tab axis is cleared, so
 * searching "acme" makes every tab count acme leads. Counting the unfiltered
 * table instead would put a 569 on a tab that opens to show three — a number
 * that is true about the database and a lie about the button it sits on.
 *
 * all === inbound + outbound + needsReview, always, because every lead has a
 * source of inbound, outbound, or nothing. That is the invariant the fourth
 * tab exists to keep, and it has its own test.
 */
export function leadTabCounts(
  leads: Lead[],
  filter: LeadFilter,
  stages: LeadStage[],
  links: LeadCategoryLink[],
  todayIso: string,
  leadsWithNextAction: ReadonlySet<string> = new Set(),
  /** Same rollup filterLeads takes, so a Won filter counts on the tabs too. */
  outcomesByLead: ReadonlyMap<string, LeadDealOutcome> = new Map(),
  /** Same rounds filterLeads takes, so a loop filter counts on the tabs too. */
  rounds: RoundContext = EMPTY_ROUNDS,
  /** Same grouping, so an origin filter counts on the tabs too. */
  originGroupById: ReadonlyMap<string, string> = new Map(),
): Record<LeadTab, number> {
  const base = filterLeads(
    leads,
    // The evidence group goes too: it only exists inside Needs Review, so
    // leaving it on would make the Inbound and Outbound tabs read zero
    // whenever somebody was part-way through working the queue.
    { ...filter, direction: null, noSourceOnly: false, evidenceGroup: null },
    stages,
    links,
    todayIso,
    leadsWithNextAction,
    new Map(),
    outcomesByLead,
    rounds,
    originGroupById,
  );
  return {
    all: base.length,
    inbound: base.filter((lead) => lead.source === 'inbound').length,
    outbound: base.filter((lead) => lead.source === 'outbound').length,
    'needs-review': base.filter((lead) => lead.source === null).length,
  };
}
